import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import axiosInstance, { BASE_URL } from "../../utils/config/config";
import { getUserById } from "../../redux/actions/user.dispatcher";
import { compressImage } from "../../utils/compressImage";

// Basic account profile for non-practitioner roles (admin, client). Edits the
// fields every Users row has — name + avatar — via the existing PUT /user/update.
// No moderation (that's practitioner-only); saves publish immediately.
export const useBasicProfile = () => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);

  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", avatar: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const notify = useCallback((severity, msg) => setSnack({ open: true, severity, msg }), []);
  const setField = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  // Seed from the signed-in user.
  useEffect(() => {
    if (userData) {
      setForm({
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        avatar: userData.avatar || "",
      });
    }
  }, [userData]);

  const handleUpload = useCallback(
    async (file) => {
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", await compressImage(file));
        const res = await axios.post(`${BASE_URL}aws/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = res.data?.fileUrl;
        if (url) setField("avatar", url);
      } catch (err) {
        notify("error", err.response?.data?.message || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [setField, notify]
  );

  const handleSubmit = useCallback(async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      notify("error", "First and last name are required.");
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.put("user/update", {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        avatar: form.avatar,
      });
      dispatch(getUserById());
      notify("success", "Profile saved");
    } catch (err) {
      notify("error", err.response?.data?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }, [form, notify, dispatch]);

  const closeSnack = useCallback(() => setSnack((s) => ({ ...s, open: false })), []);

  return { form, saving, uploading, setField, handleUpload, handleSubmit, snack, closeSnack };
};
